import React, { useEffect } from "react";
import { Container, Row, Col, Spinner, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Hero from "src/components/Hero/Hero";
import { useAccount } from "src/context/account/account.provider";
import { darkCardStyle } from "src/GlobalStyle";
import AvailablePromoCard from "src/components/Promotions/AvailablePromoCard";

const AvailableEgdPromotions = () => {
  const { t, i18n } = useTranslation();
  const {
    availablePromos,
    availablePromosLoading,
    availablePromosError,
    availablePromosLanguage,
    fetchAvailablePromosFn,
  } = useAccount();

  useEffect(() => {
    // Only fetch if promos haven't been loaded yet (null means never fetched)
    if (availablePromos === null && !availablePromosLoading) {
      fetchAvailablePromosFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch only when language changes and is different from the cached language
  useEffect(() => {
    if (Array.isArray(availablePromos) && availablePromosLanguage !== i18n.language) {
      fetchAvailablePromosFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <PageContainer>
      <Hero
        mainHeroText={t("myAccount.availablePromos.heroTitle")}
        description={t("myAccount.availablePromos.heroDescription")}
        bgImage={"about/about-us-hero-bg_v2.jpg"}
        darkenBg={true}
        className="mb-5"
      />

      <Container>
        <p className="text-center">{t("myAccount.availablePromos.checkDetails")}</p>
        <Row className="justify-content-center">
          <Col lg={10}>
            {availablePromosLoading && (
              <LoadingContainer>
                <Spinner animation="border" variant="danger" />
                <p className="mt-3">{t("myAccount.availablePromos.loading")}</p>
              </LoadingContainer>
            )}

            {availablePromosError && !availablePromosLoading && (
              <Alert variant="danger">
                <Alert.Heading>{t("myAccount.availablePromos.error")}</Alert.Heading>
                <p>{availablePromosError}</p>
                <button
                  className="btn btn-danger mt-2"
                  onClick={() => fetchAvailablePromosFn()}
                >
                  {t("myAccount.availablePromos.tryAgain")}
                </button>
              </Alert>
            )}

            {!availablePromosLoading &&
              !availablePromosError &&
              Array.isArray(availablePromos) &&
              availablePromos.length === 0 && (
                <NoPromosCard>
                  <h3>{t("myAccount.availablePromos.noPromosTitle")}</h3>
                  <p className="mt-3">
                    {t("myAccount.availablePromos.noPromosMessage")}
                  </p>
                  <Link to={t("routes.promotions")} className="btn btn-danger mt-3">
                    {t("myAccount.availablePromos.viewAllPromotions")}
                  </Link>
                </NoPromosCard>
              )}

            {!availablePromosLoading && Array.isArray(availablePromos) && availablePromos.length > 0 && (
              <PromosGrid>
                {availablePromos.map((promo) => (
                  <AvailablePromoCard key={promo.pm_promotion_id} promo={promo} />
                ))}
              </PromosGrid>
            )}
          </Col>
        </Row>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: var(--black);
  color: var(--white);
  padding-bottom: 3rem;
`;

const LoadingContainer = styled.div`
  ${darkCardStyle}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  margin: 2rem 0;
`;

const NoPromosCard = styled.div`
  ${darkCardStyle}
  text-align: center;
  padding: 3rem 2rem;
  margin: 2rem 0;
`;

const PromosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
`;

export default AvailableEgdPromotions;
